import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { publicApi } from './api.ts';
import type { Branding } from './types.ts';

type BrandingContextValue = {
  branding: Partial<Branding>;
  loading: boolean;
  reload: () => Promise<void>;
};

const FALLBACK: Branding = {
  companyName: 'Locate2u Safety & Compliance',
  tagline: 'Powered by Locate2u',
  logoUrl: '/locate2u-logo.png',
  primaryColor: '#2B93FF',
  primaryDark: '#1B6FCC',
  accentColor: '#2B93FF',
  welcomeHeading: 'Safety & Compliance',
  welcomeMessage: 'Complete your safety and compliance questionnaire to keep moving with Locate2u.',
  emailFromName: 'Locate2u Safety & Compliance',
  emailFromAddress: 'noreply@locate2u.com',
};

const BrandingContext = createContext<BrandingContextValue>({
  branding: FALLBACK,
  loading: true,
  reload: async () => {},
});

function applyCssVars(branding: Partial<Branding>): void {
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', branding.primaryColor ?? FALLBACK.primaryColor);
  root.style.setProperty('--brand-primary-dark', branding.primaryDark ?? FALLBACK.primaryDark);
  root.style.setProperty('--brand-accent', branding.accentColor ?? FALLBACK.accentColor);
  if (branding.companyName) {
    document.title = branding.companyName;
  }
}

export function BrandingProvider({ children }: { children: ReactNode }): ReactNode {
  const [branding, setBranding] = useState<Partial<Branding>>(FALLBACK);
  const [loading, setLoading] = useState(true);

  const load = useMemo(
    () => async () => {
      try {
        const res = await publicApi.get<{ branding: Partial<Branding> }>('/api/settings');
        const merged: Partial<Branding> = { ...FALLBACK, ...res.branding };
        setBranding(merged);
        applyCssVars(merged);
      } catch {
        setBranding(FALLBACK);
        applyCssVars(FALLBACK);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const value = useMemo<BrandingContextValue>(
    () => ({ branding, loading, reload: load }),
    [branding, loading, load],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}
